import { useMemo } from "react";
import { cn } from "@/lib/cn";
import { WEEKDAY_OPTIONS } from "@/types/availability";
import { formatMinutes, type SlotProjection } from "./slotProjection";
import { RAMP_DAY, RAMP_TIME, colorKeyFor, hueForKey, type ColorMode } from "./sceneTheme";

interface Props {
  projection: SlotProjection;
  colorMode: ColorMode;
  soloKey: string | null;
  onSolo: (key: string | null) => void;
}

/**
 * Legend for whatever the tiles are currently encoding — and, for the
 * categorical modes, the filter itself: clicking an entry isolates it on the
 * plate. An ordered scale gets a scale legend instead, because isolating one
 * step of a continuum is meaningless.
 *
 * Identity is never colour-alone: every entry is labelled, which is also what
 * the accessibility pass requires for two or more series.
 */
export function PreviewLegend({ projection, colorMode, soloKey, onSolo }: Props) {
  const counts = useMemo(() => tally(projection, colorMode), [projection, colorMode]);

  if (colorMode === "time" || colorMode === "day") {
    const ramp = colorMode === "time" ? RAMP_TIME : RAMP_DAY;
    const ends =
      colorMode === "time"
        ? [
            formatMinutes(projection.windowStartMinutes ?? 0),
            formatMinutes(projection.windowEndMinutes ?? 0),
          ]
        : [WEEKDAY_OPTIONS[0].label, WEEKDAY_OPTIONS[6].label];

    return (
      <div className="space-y-1">
        <div className="flex gap-[2px]">
          {ramp.map((c) => (
            <span key={c} className="h-3 flex-1 rounded-[2px]" style={{ background: c }} />
          ))}
        </div>
        <div className="flex justify-between font-mono text-[11px] text-bridge-subtle">
          <span>{ends[0]}</span>
          <span>{ends[1]}</span>
        </div>
      </div>
    );
  }

  const entries =
    colorMode === "agent"
      ? (projection.days
          .flatMap((d) => d.slots)
          .reduce<{ key: string; label: string }[]>((acc, slot) => {
            if (slot.userIndex < 0) return acc;
            const key = `agent:${slot.userIndex}`;
            if (!acc.some((e) => e.key === key)) {
              acc.push({ key, label: slot.user?.name ?? slot.user?.email ?? `Agent ${slot.userIndex + 1}` });
            }
            return acc;
          }, [])
          .sort((a, b) => a.key.localeCompare(b.key, undefined, { numeric: true })))
      : [
          { key: "status:bookable", label: "Bookable" },
          { key: "status:notice", label: "Inside notice" },
          { key: "status:horizon", label: "Past horizon" },
        ].filter((e) => (counts[e.key] ?? 0) > 0 || e.key === "status:bookable");

  if (entries.length === 0) return null;

  return (
    <ul className="flex flex-wrap gap-1">
      {entries.map((entry) => {
        const selected = soloKey === entry.key;
        return (
          <li key={entry.key}>
            <button
              type="button"
              aria-pressed={selected}
              onClick={() => onSolo(selected ? null : entry.key)}
              className={cn(
                "flex items-center gap-1.5 rounded-md border px-2 py-1 text-[12.5px] transition-colors",
                selected
                  ? "border-bridge-accent bg-bridge-accent/5 font-semibold text-bridge-fg"
                  : "border-transparent text-bridge-muted hover:bg-bridge-bg",
              )}
            >
              <span
                className="h-3 w-3 shrink-0 rounded-[2px]"
                style={{
                  background: hueForKey(entry.key, projection.active),
                  boxShadow: "inset 0 0 0 1px rgba(13,21,36,.22)",
                }}
              />
              <span className="max-w-[92px] truncate">{entry.label}</span>
              <span className="font-mono tabular-nums text-bridge-subtle">
                {counts[entry.key] ?? 0}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

/** How many tiles fall in each colour bucket, for the legend's counts. */
function tally(projection: SlotProjection, mode: ColorMode): Record<string, number> {
  const counts: Record<string, number> = {};
  let ordinal = 0;
  for (const day of projection.days) {
    day.slots.forEach((slot, rowIndex) => {
      const key = colorKeyFor(slot, mode, ordinal++, rowIndex, day.slots.length);
      counts[key] = (counts[key] ?? 0) + 1;
    });
  }
  return counts;
}
